<script setup lang="ts">
import '@/pages/inventory/ElementsPassportV2/ui/assets/style.scss'
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  buildGeoJsonGeometryString,
  statusDateToClientLocal,
  useHelpers,
  useTranslate,
} from '@/shared/lib'
import { useNotification, VCopy, VIcons } from '@/shared/ui'
import { InventAcceptStatusButton } from '@/features/Inventory/AcceptStatusButton'
import { AcceptStatus } from '@/shared/ui/VInventAccentStatus/model/types'
import { InventPassportSaveDateModal } from '@/features/Inventory/PassportSaveDateModal'
import { usePassportRuntimeV2 } from '../model/usePassportRuntimeV2'
import { validateAllMandatoryFieldValues } from '../model/validateMandatoryFields'
import { useMandatoryFieldHighlight } from '../model/useMandatoryFieldHighlight'
import PassportRuntimeV2Body from './PassportRuntimeV2Body.vue'
import { usePassportRuntimeRelated } from '../model/usePassportRuntimeRelated'
import {
  coordsPresent,
  navigateToMapView,
  navigateToPlaceOnMap,
} from '@/features/Inventory/PlaceOnMap'
import { useCurrentElementType } from '@/entities/Inventory/Main/model/useCurrentElementType'
import PassportRuntimeV2FieldList from '@/features/Inventory/PassportRuntimeV2/ui/PassportRuntimeV2FieldList.vue'
import { resolvePassportPermissionTypeIds, useAuth } from '@/entities/Auth'
import { useRemoveInventObject } from '@/features/Inventory/ObjectRemove'

const props = defineProps<{
  elementId?: number | string
  objectId?: number | string
  modal?: boolean
}>()
const emit = defineEmits<{
  (e: 'close'): void
}>()

const route = useRoute()
const router = useRouter()
const { trs } = useTranslate()
const { toTime } = useHelpers()
const { addError } = useNotification()
const { canPassportDetailedViewForTypes, canPassportUpdateDetailedViewForTypes } = useAuth()
const objectId = computed(() => String(props.objectId ?? route.params.object_id ?? ''))
const elementId = computed(() => Number(props.elementId ?? route.params.invent_object_id))
const filter = ref<{ status_date: string | null }>({ status_date: null })

const {
  isLoading,
  hasError,
  error,
  isSaving,
  isDirty,
  layout,
  element,
  statusDates,
  fieldValues,
  fieldGroups,
  fieldMetadata,
  layoutFieldMetadata,
  linkedDisplayByGridId,
  linkedValuesLoading,
  useNormalizedPassport,
  normalizedPassportPayload,
  pseudosqlValues,
  recomputePseudosql,
  save,
  refetch,
} = usePassportRuntimeV2(elementId, objectId, filter)

const {
  relatedObjects,
  isLoading: relatedLoading,
  objectLinkFields,
} = usePassportRuntimeRelated(
  fieldMetadata,
  fieldValues,
  computed(() => filter.value.status_date),
)

const hasRelatedTab = computed(() => objectLinkFields.value.length > 0)

const nameFieldRef = ref<any>(null)
const isEditName = ref(false)
const detailedViewOpen = ref(false)
/**
 * Drawer scroll target (leaf field id); cleared after scroll inside `PassportRuntimeV2AllFieldsDrawer`.
 * Self-check: parent/unsupported pills open drawer + `data-field-id` scroll; copy uses raw `fieldValues` + empty toast.
 */
const focusFieldId = ref<string | null>(null)

function openAllFieldsDrawer(fieldId?: string | null) {
  if (!canOpenDetailedView.value) return
  focusFieldId.value = fieldId ?? null
  detailedViewOpen.value = true
}

function isAcceptStatusKey(s: string): s is keyof typeof AcceptStatus {
  return Object.prototype.hasOwnProperty.call(AcceptStatus, s)
}

/** Steady-state workflow status for chip when object requires accept and API returned a known status. */
const workflowEnabled = computed(() =>
  Boolean(element.value?.invent_object_type?.properties?.is_must_accept),
)
const workflowStatus = computed(() => String(element.value?.accept_status ?? ''))

/** Steady-state workflow status for chip when object requires accept and API returned a known status. */
const workflowAcceptStatus = computed(() => {
  if (hasError.value || isLoading.value || isSaving.value || isDirty.value) return null
  if (!workflowEnabled.value) return null
  const s = element.value?.accept_status
  if (typeof s !== 'string' || !isAcceptStatusKey(s)) return null
  return s
})

const statusChipLabel = computed(() => {
  if (hasError.value) return String(error.value ?? trs('itf.unknown_error'))
  if (isLoading.value) return 'Загрузка'
  if (isSaving.value) return 'Сохранение'
  if (isDirty.value) return 'Есть несохранённые изменения'
  const ws = workflowAcceptStatus.value
  if (ws) return AcceptStatus[ws].name
  return 'Сохранено'
})

const statusChipColor = computed(() => {
  if (hasError.value) return 'error'
  if (isDirty.value) return 'warning'
  if (isLoading.value || isSaving.value) return 'success'
  const ws = workflowAcceptStatus.value
  if (ws) {
    switch (ws) {
      case 'DRAFT':
        return 'grey'
      case 'PREACCEPT':
        return 'warning'
      case 'REWORK':
        return 'info'
      case 'ACCEPTED':
        return 'success'
      case 'CANCELLED':
        return 'error'
      default:
        return 'success'
    }
  }
  return 'success'
})

const elementHasMapCoordinates = computed(() => coordsPresent(element.value?.coordinates))

/**
 * Выводим первые координаты элемента с укороченной длинной
 */
const displayCoords = computed(() => {
  const coords = element.value?.coordinates?.[0]
  if (!coords) return ''

  return coords.map((c: number) => String(c).slice(0, 6) + '...').join(', ')
})

function onMapToolbarAction() {
  const id = Number(element.value?.id)
  if (!Number.isFinite(id) || id <= 0) return
  if (elementHasMapCoordinates.value) {
    navigateToMapView(router, route, id)
  } else {
    navigateToPlaceOnMap(router, route, id)
  }
}

const statusDatesList = computed(() => [
  {
    title: trs('itf.now_date'),
    value: null,
    status: null,
  },
  ...(statusDates.value as Array<{ date: string; status: string | null; user_name?: string }>).map(
    (item) => ({
      title: (() => {
        const local = statusDateToClientLocal(item.date)
        return local.isValid
          ? local.toFormat('dd.LL.yyyy HH:mm')
          : toTime(item.date).toFormat('dd.LL.yyyy HH:mm')
      })(),
      value: item.date,
      status: item.status,
      userName: item.user_name ?? '',
    }),
  ),
])
/** Non-empty snapshot date locks save; treat '', null, undefined as editable «текущая». */
const hasVersionLockedSave = computed(() => {
  if (workflowEnabled.value) {
    return (
      workflowStatus.value === 'ACCEPTED' ||
      workflowStatus.value === 'PREACCEPT' ||
      workflowStatus.value === 'CANCELLED'
    )
  }
  const d = filter.value.status_date
  return d != null && String(d).trim().length > 0
})

/**
 * Do not gate on `layout.isValid`: the hero + «Подробный вид» drawer stay mounted when the grid
 * is missing or invalid, and edits there still mutate `fieldValues` → `isDirty` without a way to save.
 * Persisting uses `element` + `fieldValues` like the normal body.
 */
const mandatoryValidation = computed(() =>
  validateAllMandatoryFieldValues(fieldValues.value, fieldMetadata.value ?? []),
)

const { invalidFieldIds, applyMandatoryValidationResult, clearMandatoryFieldHighlight } =
  useMandatoryFieldHighlight(fieldValues, fieldMetadata)

const canSave = computed(
  () =>
    Boolean(element.value?.id) && isDirty.value && !isSaving.value && !hasVersionLockedSave.value,
)
const canPrimaryAction = computed(() => {
  if (!Boolean(element.value?.id) || isSaving.value) return false
  if (workflowEnabled.value && workflowStatus.value === 'ACCEPTED') {
    return true
  }
  return canSave.value
})
const showSaveButton = computed(() => {
  if (!workflowEnabled.value) return true
  if (workflowStatus.value === 'PREACCEPT') return false
  return (
    workflowStatus.value === 'ACCEPTED' ||
    workflowStatus.value === 'DRAFT' ||
    workflowStatus.value === 'REWORK'
  )
})
const saveButtonAria = computed(() => {
  if (!workflowEnabled.value) return String(trs('itf.save'))
  if (workflowStatus.value === 'ACCEPTED') return 'Создать черновик'
  if (workflowStatus.value === 'DRAFT' || workflowStatus.value === 'REWORK')
    return 'Сохранить черновик'
  return String(trs('itf.save'))
})
const catalogTypeId = computed(
  () =>
    Number(element.value?.invent_objects_type_id) ||
    Number(element.value?.invent_object_type?.id) ||
    0,
)
const passportPermissionTypeIds = computed(() =>
  resolvePassportPermissionTypeIds({
    objectTypeId: element.value?.object_type?.id,
    inventObjectsTypeId: element.value?.invent_objects_type_id,
  }),
)
const canOpenDetailedView = computed(
  () =>
    !isLoading.value &&
    passportPermissionTypeIds.value.length > 0 &&
    canPassportDetailedViewForTypes(passportPermissionTypeIds.value),
)
const canEditDetailedView = computed(
  () =>
    !isLoading.value &&
    passportPermissionTypeIds.value.length > 0 &&
    canPassportUpdateDetailedViewForTypes(passportPermissionTypeIds.value),
)
const detailedViewFieldsLocked = computed(
  () => hasVersionLockedSave.value || !canEditDetailedView.value,
)
const { setCurrentElementTypeId } = useCurrentElementType()

watch(
  element,
  (val) => {
    const id = Number(val?.object_type?.id) || null
    setCurrentElementTypeId(id)
  },
  { immediate: true },
)

/**
 * Возвращает к списку или закрывает паспорт, если он открыт в модальном окне.
 */
const goBack = () => {
  if (props.modal) {
    emit('close')
    return
  }

  if (window.history.state?.back != null) {
    router.back()
    return
  }
  router.push({
    name: 'object.inventory.elements',
    params: { ...route.params, tab_inventory: 'elements' },
  })
}

const saveDateModalOpen = ref(false)

async function runSave(verDate?: string) {
  if (!mandatoryValidation.value.valid) {
    applyMandatoryValidationResult(mandatoryValidation.value)
    addError(trs('itf.mandatory_fields_missing'))
    return
  }
  clearMandatoryFieldHighlight()
  const ok = await save(verDate)
  if (!ok) return
  await refetch()
}

async function onSave() {
  saveDateModalOpen.value = true
}

async function onSaveWithDate(verDate: string | undefined) {
  await runSave(verDate)
}

function startEditName() {
  isEditName.value = true
  nextTick(() => {
    const input =
      nameFieldRef.value?.$el?.querySelector('input') || nameFieldRef.value?.$refs?.input
    input?.focus()
    input?.select()
  })
}

function finishEditName() {
  isEditName.value = false
}

function getColorStatus(status: string | null) {
  if (!status) return undefined
  const color = AcceptStatus[status]?.color ?? '25, 118, 210'
  return `rgba(${color}, 0.7)`
}

function getDateDotColorNoWorkflow(rawDate: string | null | undefined) {
  if (!rawDate) return undefined
  const dt = statusDateToClientLocal(String(rawDate))
  if (!dt?.isValid) return 'rgba(158, 158, 158, 0.85)'
  const nowMs = Date.now()
  return dt.toMillis() <= nowMs ? 'rgba(76, 175, 80, 0.85)' : 'rgba(158, 158, 158, 0.85)'
}

/** Открывает списка со списком полей и прокручивает к форме изменения координат. */
const fieldListRef = ref<InstanceType<typeof PassportRuntimeV2FieldList> | null>(null)

const editCoordsHandler = () => {
  fieldListRef.value?.scrollToBottom()
}

/**
 * Функция копирования координат в формате geoJSON
 */
const copyCoordsElem = () =>
  buildGeoJsonGeometryString(element.value?.coordinates as [number, number][])

/**
 * Функция удаления инвентаризационного элемента
 */
const { removeSimple } = useRemoveInventObject()
const deleteHandler = () => {
  if (!element.value) return

  removeSimple(Number(element.value.id)).then(() => {
    router.push({
      name: 'object.inventory.type.elements',
      params: {
        ...route.params,
        invent_type_id: element.value?.object_type?.id,
        tab_inventory: 'elements',
      },
    })
  })
}
</script>

<template>
  <section class="passport-runtime-v2">
    <header class="passport-runtime-v2__hero">
      <VBtn
        variant="text"
        density="comfortable"
        class="passport-runtime-v2__back-btn"
        :aria-label="trs('itf.back_list')"
        @click="goBack"
      >
        <VIcons type="arrow_back" />
      </VBtn>

      <div class="passport-runtime-v2__hero-copy">
        <div class="passport-runtime-v2__title-row">
          <template v-if="isLoading">
            <VSkeletonLoader type="heading" class="passport-runtime-v2__title-skeleton" />
          </template>
          <template v-else-if="hasVersionLockedSave">
            <VTextField
              :model-value="element.name"
              :label="trs('itf.name_label')"
              variant="outlined"
              density="compact"
              hide-details
              readonly
              class="passport-runtime-v2__name-inline passport-runtime-v2__name-inline--readonly"
            />
          </template>
          <template v-else-if="!isEditName">
            <div class="passport-runtime-v2__title-group">
              <h1 class="passport-runtime-v2__title">
                {{ element.name }}
              </h1>
              <VTooltip location="top">
                <template #activator="{ props: tipProps }">
                  <VBtn
                    class="passport-runtime-v2__edit-btn"
                    icon
                    variant="text"
                    size="x-small"
                    v-bind="tipProps"
                    :aria-label="trs('itf.edit')"
                    @click="startEditName"
                  >
                    <VIcons type="edit" />
                  </VBtn>
                </template>
                <span>{{ trs('itf.edit') }}</span>
              </VTooltip>
            </div>
          </template>
          <template v-else>
            <VTextField
              ref="nameFieldRef"
              v-model="element.name"
              :label="trs('itf.name_label')"
              variant="outlined"
              density="compact"
              hide-details
              class="passport-runtime-v2__name-inline"
              @keydown.enter.prevent="finishEditName"
              @blur="finishEditName"
            />
          </template>
        </div>
      </div>

      <div class="passport-runtime-v2__actions">
        <div class="passport-runtime-v2__controls">
          <VChip size="small" variant="tonal" :color="statusChipColor">
            {{ statusChipLabel }}
          </VChip>

          <VSelect
            v-model="filter.status_date"
            :items="statusDatesList"
            :label="trs('itf.status_dates')"
            variant="outlined"
            density="compact"
            hide-details
            class="passport-runtime-v2__status-select"
          >
            <template #item="{ props, item }">
              <VListItem v-bind="props">
                <template #title>
                  <div
                    v-if="workflowEnabled && item.raw.status"
                    class="passport-runtime-v2__status-option"
                  >
                    <div class="passport-runtime-v2__status-option-line">
                      <div
                        class="passport-runtime-v2__status-dot"
                        :style="{ 'background-color': getColorStatus(item.raw.status) }"
                      />
                      {{ item.title }}
                    </div>
                    <div
                      v-if="item.raw.userName"
                      class="passport-runtime-v2__status-user"
                    >
                      {{ item.raw.userName }}
                    </div>
                  </div>
                  <div
                    v-else-if="!workflowEnabled && item.raw.value"
                    class="passport-runtime-v2__status-option"
                  >
                    <div class="passport-runtime-v2__status-option-line">
                      <div
                        class="passport-runtime-v2__status-dot"
                        :style="{
                          'background-color': getDateDotColorNoWorkflow(String(item.raw.value)),
                        }"
                      />
                      {{ item.title }}
                    </div>
                    <div
                      v-if="item.raw.userName"
                      class="passport-runtime-v2__status-user"
                    >
                      {{ item.raw.userName }}
                    </div>
                  </div>
                  <div v-else>{{ item.title }}</div>
                </template>
              </VListItem>
            </template>
          </VSelect>

          <InventAcceptStatusButton
            v-if="workflowEnabled && (workflowStatus === 'DRAFT' || workflowStatus === 'REWORK')"
            appearance="toolbar"
            toolbar-mode="submit"
            :status="String(element.accept_status ?? '')"
            :status_date="filter.status_date"
            @updated="refetch"
          />
          <InventAcceptStatusButton
            v-else-if="workflowEnabled && workflowStatus === 'PREACCEPT'"
            appearance="toolbar"
            toolbar-mode="review"
            :status="String(element.accept_status ?? '')"
            :status_date="filter.status_date"
            @updated="refetch"
          />

          <VTooltip location="top">
            <template #activator="{ props: tipProps }">
              <VBtn
                v-bind="tipProps"
                variant="outlined"
                :aria-label="elementHasMapCoordinates ? trs('itf.map') : trs('itf.place_on_map')"
                @click="onMapToolbarAction"
              >
                <VIcons :type="elementHasMapCoordinates ? 'location_on' : 'add_location_alt'" />
              </VBtn>
            </template>
            <span>{{ elementHasMapCoordinates ? trs('itf.map') : trs('itf.place_on_map') }}</span>
          </VTooltip>

          <VTooltip v-if="canOpenDetailedView" location="top">
            <template #activator="{ props: tipProps }">
              <VBtn
                v-bind="tipProps"
                variant="outlined"
                :disabled="hasError"
                :aria-label="trs('itf.passport_runtime_detailed_view_title')"
                @click="openAllFieldsDrawer(null)"
              >
                <VIcons type="view_list" />
              </VBtn>
            </template>
            <span>{{ trs('itf.passport_runtime_detailed_view_title') }}</span>
          </VTooltip>

          <VTooltip location="top">
            <template #activator="{ props: tipProps }">
              <VBtn v-bind="tipProps" variant="tonal" color="error" @click="deleteHandler">
                <VIcons type="archive" />
              </VBtn>
            </template>

            <span>{{ trs('itf.archive') }}</span>
          </VTooltip>

          <VBtn
            color="primary"
            variant="flat"
            :loading="isSaving"
            :disabled="!canPrimaryAction"
            :aria-label="saveButtonAria"
            @click="onSave"
          >
            {{ $t('itf.save') }}
          </VBtn>
        </div>
      </div>

      <div v-if="element.object_type?.object_name" class="passport-runtime-v2__type-name">
        {{ element.object_type.object_name }}
        <span class="passport-runtime-v2__coords" v-if="element.coordinates">
          Координаты: <VCopy :copy-data-func="copyCoordsElem">{{ displayCoords }}</VCopy>

          <VIcons type="edit" class="passport-runtime-v2__coords_edit" @click="editCoordsHandler" />
        </span>
      </div>
    </header>
    <VAlert
      v-if="hasError"
      class="passport-runtime-v2__state"
      type="error"
      variant="tonal"
      :icon="false"
      :title="trs('itf.unknown_error')"
      :text="String(error ?? trs('itf.unknown_error'))"
    >
      <template #prepend>
        <VIcons type="error" />
      </template>
      <template #append>
        <VBtn variant="text" @click="refetch()">Повторить</VBtn>
      </template>
    </VAlert>

    <div
      v-else-if="isLoading"
      class="passport-runtime-v2__state passport-runtime-v2__state--loading"
    >
      <VSkeletonLoader type="heading, paragraph, paragraph, paragraph" />
    </div>

    <VAlert
      v-else-if="!layout.isValid"
      class="passport-runtime-v2__state"
      type="warning"
      variant="tonal"
      :icon="false"
      :title="trs('itf.passport_runtime_no_layout_title')"
      :text="trs('itf.passport_runtime_no_layout_text')"
    >
      <template #prepend>
        <VIcons type="warning" />
      </template>
    </VAlert>

    <div v-else class="passport-runtime-v2__body">
      <PassportRuntimeV2Body
        :tabs="layout.tabs"
        :field-values="fieldValues"
        :catalog-type-id="catalogTypeId"
        :fields-locked="hasVersionLockedSave"
        :stats="element.stats"
        :all-fields="layoutFieldMetadata"
        :linked-display-values="linkedDisplayByGridId"
        :linked-values-loading="linkedValuesLoading"
        :object-id="objectId"
        :element-id="elementId"
        :filter="filter"
        :field-groups="fieldGroups"
        :has-related-tab="hasRelatedTab"
        :related-objects="relatedObjects"
        :related-loading="relatedLoading"
        :invalid-mandatory-field-ids="invalidFieldIds"
        :can-open-detailed-view="canOpenDetailedView"
        :field-selection-locked="detailedViewFieldsLocked"
        :use-normalized-fields="useNormalizedPassport"
        :normalized-passport-payload="normalizedPassportPayload"
        :pseudosql-values="pseudosqlValues"
        @open-drawer="openAllFieldsDrawer"
        @recompute-pseudosql="recomputePseudosql"
      />
    </div>

    <!-- related objects rendered inside PassportRuntimeV2Body as a tab -->
    <PassportRuntimeV2FieldList
      v-if="canOpenDetailedView"
      ref="fieldListRef"
      v-model:open="detailedViewOpen"
      v-model:fields="fieldValues"
      v-model:element="element"
      :fields-meta="fieldMetadata"
      :fields-locked="detailedViewFieldsLocked"
    />

    <InventPassportSaveDateModal v-model:show="saveDateModalOpen" @apply="onSaveWithDate" />
  </section>
</template>

<style scoped></style>
